// ── Change plan ──
// What a subscriber sees on /billing (reached from Membership → Change plan): every plan as a card,
// theirs marked, and what moving to each one does. Presentational like PlanPicker: prices come from
// /api/plans, what they hold from /api/subscription, and every amount they pay from the server's quote.
//
//   UP    now, for the difference for the days left (quote-upgrade → the ordinary pay).
//   DOWN  at the next renewal (a plan that renews by autopay only). It removes every feature the lower
//         plan does not list — including ones they KEPT from before a feature moved to a higher plan —
//         so the loss is listed and confirmed before anything is scheduled.
import React, { useEffect, useRef, useState } from 'react';
import { INR } from './plans.api.js';
import { card, h2, sub, field, primaryBtn, ghostBtn, label, note } from './billing.styles.js';

const box = { margin: '0 0 18px', padding: '14px 16px', border: '1px solid var(--accent)', borderRadius: 10 };
const h3 = { fontSize: 16, margin: '0 0 6px' };
const small = { fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 };

export default function ChangePlan({ plans, features, states, subscription, busy, onQuote, onDowngrade, onCancelDowngrade }) {
  const [billingState, setBillingState] = useState('');
  // Buttons are never greyed out for a missing state (CLAUDE.md, front-end standard 7): pressing one says so here,
  // outlines the field and moves focus to it.
  const [stateMissing, setStateMissing] = useState(false);
  const stateRef = useRef(null);
  const withState = (go) => () => {
    if (!billingState) { setStateMissing(true); if (stateRef.current) stateRef.current.focus(); return; }
    go();
  };
  const [confirmDown, setConfirmDown] = useState(null);   // the lower plan key awaiting "yes, move down"
  // The confirmation appears below the cards: take focus there, so a keyboard or screen-reader user
  // is not left on the button above it without hearing what a move down removes.
  const askRef = useRef(null);
  useEffect(() => { if (confirmDown && askRef.current) askRef.current.focus(); }, [confirmDown]);

  // By RANK, not display order (/api/plans is ordered by `sort`, which an admin may change without
  // changing rank) — "up" and "down" are rank questions.
  const byRank = [...plans].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  const at = byRank.findIndex((x) => x.key === subscription.plan);
  const cur = byRank[at];
  const ch = subscription.change || {};
  const kept = ch.kept || [];
  const next = ch.nextPlan && plans.find((x) => x.key === ch.nextPlan);
  const yearly = ch.product === 'annual';
  const labelOf = (k) => ((features || []).find((f) => f.key === k) || {}).label || k;
  const curName = (cur && cur.name) || subscription.plan;
  const lockIn = !!(subscription.priceNotice && subscription.priceNotice.annualLockIn && ch.canSwitchAnnual !== false);
  // The billing state is asked once, for whichever of these they pay for.
  const needsState = (at >= 0 && byRank.length > at + 1) || !!ch.renewal || !!ch.canSwitchAnnual || lockIn;

  // What moving down to `lower` takes away: their plan's features the lower one lacks, plus everything kept.
  const lostTo = (lower) => {
    const has = new Set(lower.features || []);
    return [...new Set([...((cur && cur.features) || []), ...kept])].filter((k) => !has.has(k))
      .map((k) => ({ key: k, label: labelOf(k), kept: kept.includes(k) }));
  };
  const priceOf = (p) => (yearly ? p.annual : p.monthly);
  const overAutopay = (p) => ch.renews && ch.autopayLimit && priceOf(p) && priceOf(p).total > ch.autopayLimit;

  const pending = confirmDown && byRank.find((x) => x.key === confirmDown);
  const lost = pending ? lostTo(pending) : [];

  return (
    <section id="change" style={card}>
      <h2 style={h2}>Change plan</h2>
      <p style={sub}>
        {/* Named here as well as by the card's tag: a plan no longer sold has no card. */}
        You are on {curName}{subscription.paidThrough ? `, paid through ${subscription.paidThrough}` : ''}. Move up whenever you like: you pay the difference for the days left, and the higher plan starts as soon as
        that payment clears. {ch.renews
          ? `Moving down takes effect at your next renewal, after ${subscription.paidThrough}.`
          : `Your plan does not renew on its own, so to move down, choose the lower plan when it ends on ${subscription.paidThrough}.`}
      </p>

      {ch.renewal && (
        // Renewing by hand: a prepaid plan, or an autopay renewal the mandate cannot or will not collect.
        <div id="renew" style={box}>
          <h3 style={h3}>{ch.renewal.why && ch.renewal.why !== 'prepaid' ? 'Pay your renewal' : `Renew for another ${yearly ? 'year' : 'month'}`}</h3>
          {ch.renewal.open ? (
            <>
              <p style={note}>{renewWhy(ch.renewal, subscription.paidThrough)}</p>
              <button style={{ ...primaryBtn, opacity: busy ? 0.55 : 1 }} disabled={busy}
                onClick={withState(() => onQuote({ renew: true, billingState }))}>See the renewal total</button>
            </>
          ) : (
            <p style={note}>Your plan does not renew on its own. You can renew it from {ch.renewal.opensOn}.</p>
          )}
        </div>
      )}

      {needsState && (
        <label style={{ display: 'block', margin: '0 0 18px', maxWidth: 360 }}>
          <span style={label}>Where you are billed</span>
          <select ref={stateRef} style={stateMissing ? { ...field, border: '1px solid var(--loss)' } : field} aria-label="Where you are billed"
            aria-invalid={stateMissing || undefined} aria-describedby="cp-state-help" value={billingState}
            onChange={(e) => { setBillingState(e.target.value); if (e.target.value) setStateMissing(false); }}>
            <option value="">Choose your state</option>
            {Object.entries(states || {}).sort((a, b) => String(a[1]).localeCompare(String(b[1])))
              .map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
          {stateMissing
            ? <small id="cp-state-help" role="alert" style={{ ...small, display: 'block', marginTop: 6, color: 'var(--loss)' }}>Choose the state you are billed in first: it decides the tax on what you pay.</small>
            : <small id="cp-state-help" style={{ ...small, display: 'block', marginTop: 6 }}>Needed for the tax on anything you pay below.</small>}
        </label>
      )}

      <div role="list" aria-label="Plans" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', margin: '0 0 18px' }}>
        {byRank.map((p, i) => {
          const isCur = i === at;
          const up = at >= 0 && i > at;
          const price = priceOf(p);
          return (
            <div role="listitem" key={p.key} aria-label={p.name} style={{
              position: 'relative', padding: '18px 16px 16px', borderRadius: 12, background: 'var(--surface2)',
              border: `1px solid ${isCur ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              {isCur && <span style={tag}>Current</span>}
              {next && next.key === p.key && <span style={{ ...tag, background: 'var(--amber)' }}>From your renewal</span>}
              <strong style={{ fontSize: 16.5 }}>{p.name}</strong>
              <span style={small}>{p.tagline}</span>
              {price && (
                <span style={{ marginTop: 8 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 19, fontWeight: 700 }}>{INR(price.base)}</span>
                  <small style={{ ...small, fontWeight: 500 }}> a {yearly ? 'year' : 'month'} + GST</small>
                </span>
              )}
              <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, fontSize: 13.5, color: 'var(--muted)' }}>
                <li style={row}><span>Trading accounts</span><strong style={{ color: 'var(--text)' }}>{p.accountCap}</strong></li>
                <li style={row}><span>Features</span><strong style={{ color: 'var(--text)' }}>{(p.features || []).length}</strong></li>
                {(yearly ? p.monthly : p.annual) && (
                  <li style={row}><span>{yearly ? 'Monthly' : 'Yearly'}</span><span>{INR((yearly ? p.monthly : p.annual).base)} + GST</span></li>
                )}
              </ul>

              {isCur && kept.length > 0 && (
                <p style={{ ...small, margin: '8px 0 0' }}>
                  Also yours, kept from before: {kept.map(labelOf).join(', ')}.
                </p>
              )}

              {up && (
                <div style={{ marginTop: 'auto', paddingTop: 12 }}>
                  {overAutopay(p) && (
                    // Said BEFORE the click: past the per-debit limit, autopay cannot collect the renewal.
                    <p style={{ ...small, color: 'var(--amber)', margin: '0 0 8px' }}>
                      More than the {INR(ch.autopayLimit)} your bank lets autopay collect. After moving up, your plan
                      no longer renews on its own: we remind you before it ends and you renew it yourself.
                    </p>
                  )}
                  <button style={{ ...primaryBtn, width: '100%', opacity: busy ? 0.55 : 1 }} disabled={busy}
                    onClick={withState(() => onQuote({ plan: p.key, billingState, upgrade: true }))}>Move up to {p.name}</button>
                </div>
              )}

              {!isCur && !up && at >= 0 && (
                <div style={{ marginTop: 'auto', paddingTop: 12 }}>
                  {next && next.key === p.key ? (
                    <button style={{ ...ghostBtn, width: '100%' }} disabled={busy} onClick={onCancelDowngrade}>Keep {curName}</button>
                  ) : next ? null : ch.renews ? (
                    <button style={{ ...ghostBtn, width: '100%' }} disabled={busy} onClick={() => setConfirmDown(p.key)}>
                      Move down at renewal
                    </button>
                  ) : (
                    <p style={{ ...small, margin: 0 }}>Choose it when your plan ends on {subscription.paidThrough}.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pending && (
        // Confirmed before anything is scheduled: a move down removes features, including kept ones.
        <div role="alertdialog" aria-label={`Move down to ${pending.name}`} ref={askRef} tabIndex={-1}
          style={{ ...box, borderColor: 'var(--amber)', outline: 'none' }}>
          <h3 style={h3}>Move to {pending.name} at your renewal?</h3>
          <p style={note}>
            You keep everything on {curName} until {subscription.paidThrough}. From your renewal you pay the {pending.name} price
            {pending.accountCap < ((cur && cur.accountCap) || 0)
              ? `, and you can have up to ${pending.accountCap} trading account${pending.accountCap === 1 ? '' : 's'} (the ones already connected stay connected; you cannot add more)` : ''}.
          </p>
          {lost.length > 0 && (
            <>
              <p style={{ ...note, color: 'var(--text)', marginBottom: 6 }}>You lose these {lost.length === 1 ? 'feature' : `${lost.length} features`}:</p>
              <ul style={{ margin: '0 0 12px', paddingLeft: 20, ...small, color: 'var(--text)', columns: '2 220px', columnGap: 28 }}>
                {lost.map((f) => (
                  <li key={f.key}>{f.label}{f.kept ? <span style={{ color: 'var(--amber)' }}> — kept from before, when your plan still included it</span> : ''}</li>
                ))}
              </ul>
            </>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button style={primaryBtn} disabled={busy} onClick={() => { onDowngrade(pending.key); setConfirmDown(null); }}>
              Move to {pending.name} at renewal
            </button>
            <button style={ghostBtn} disabled={busy} onClick={() => setConfirmDown(null)}>Keep {curName}</button>
          </div>
        </div>
      )}

      {lockIn ? (
        // The lock-in offer that came with a price rise: a year at today's price, this month credited.
        <div style={box}>
          <h3 style={h3}>Lock in today's price</h3>
          <p style={note}>
            Your plan renews at {INR(subscription.priceNotice.newPrice)} a month from {subscription.priceNotice.appliesOn}.
            Switch to yearly before {subscription.priceNotice.annualLockIn.until} and pay {INR(subscription.priceNotice.annualLockIn.price)} for
            the next 12 months — the unused part of this month is taken off.
          </p>
          <button style={{ ...primaryBtn, opacity: busy ? 0.55 : 1 }} disabled={busy}
            onClick={withState(() => onQuote({ switchAnnual: true, billingState }))}>See the yearly total</button>
        </div>
      ) : ch.canSwitchAnnual && (
        // Any monthly plan may move to yearly now.
        <div style={{ margin: '0 0 6px' }}>
          <h3 style={h3}>Switch to yearly</h3>
          <p style={note}>
            Pay for a year from today{cur && cur.annual && cur.annual.savingPct ? ` and save ${cur.annual.savingPct}%` : ''}. The
            unused part of this month is taken off, and your plan stays the same.
          </p>
          <button style={{ ...ghostBtn, opacity: busy ? 0.55 : 1 }} disabled={busy}
            onClick={withState(() => onQuote({ switchAnnual: true, billingState }))}>See the yearly total</button>
        </div>
      )}
    </section>
  );
}

const tag = {
  position: 'absolute', top: -9, left: 14, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  background: 'var(--accent)', color: 'var(--accent-ink)', padding: '3px 8px', borderRadius: 4,
};
const row = { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderTop: '1px solid var(--border)' };

/** Why this renewal is paid by hand, in the customer's terms (checkout.renewalWindow's `why`). */
function renewWhy(r, paidThrough) {
  if (r.why === 'needs_reauth') {
    return `Your next renewal is larger than your bank lets us collect automatically, so it needs you to pay it. `
      + `Pay it by ${r.closesOn} and your plan carries on with no gap; your autopay stays set up for the renewals after it.`;
  }
  if (r.why === 'abandoned') {
    return `We could not collect your renewal from your autopay and have stopped trying. Pay it by ${r.closesOn} `
      + `and your plan carries on with no gap.`;
  }
  return `Your plan does not renew on its own. Renew now and it carries on after ${paidThrough} with no gap. `
    + `You are not charged again until you choose to renew.`;
}
